<script setup lang="ts">
import SidebarItem from "@/components/MyStudents/Navigation/SidebarItem.vue";
import {
  hasDiscountPricingPermission,
  hasDisplayAdminUIForStudentDiscountsEnabled,
  hasItemsPermissionProgramManager,
  hasProgramsReadPermission,
} from "@/composables/authorization";
import { PROGRAM_MANAGER_V2_ROUTES } from "@/constants.ts";
import type { NavigationSectionProps } from "../types.ts";

const props = withDefaults(defineProps<NavigationSectionProps>(), {
  showHiddenNav: false,
  showHeader: false,
});

const emit = defineEmits(["menu-item-clicked"]);

defineOptions({ name: "LibrarySection" });

const handleMenuItemClick = () => {
  emit("menu-item-clicked");
};
</script>

<template>
  <div data-testid="library-section">
    <div v-if="showHeader" class="mt-4" data-testid="library-title">
      <h2
        class="font-montserrat font-bold text-university-primary mb-2"
      >
        Library
      </h2>
      <hr class="border-t border-gray-475 mb-2" />
    </div>
    <ul class="space-y-2" data-testid="library-list">
      <SidebarItem
        v-if="hasProgramsReadPermission"
        text="Programs"
        :router-link="{ name: 'program-manager-programs' }"
        data-test-id="program-manager-programs"
        @menu-item-clicked="handleMenuItemClick"
        mui-icon-name="import_contacts"
        :enable-selected-links="true"
        :count="counts?.get('program-manager-programs')"
      />
      <SidebarItem
        v-if="hasProgramsReadPermission"
        text="Program Sessions"
        :router-link="{ name: PROGRAM_MANAGER_V2_ROUTES.PROGRAM_SESSIONS }"
        data-test-id="sessions"
        @menu-item-clicked="handleMenuItemClick"
        mui-icon-name="auto_stories"
        :enable-selected-links="true"
        :count="counts?.get(PROGRAM_MANAGER_V2_ROUTES.PROGRAM_SESSIONS)"
      />
      <SidebarItem
        v-if="hasItemsPermissionProgramManager"
        text="Housing"
        :router-link="{ name: 'program-manager-housing' }"
        data-test-id="housing"
        @menu-item-clicked="handleMenuItemClick"
        mui-icon-name="apartment"
        :enable-selected-links="true"
        :count="counts?.get('program-manager-housing')"
      />
      <SidebarItem
        v-if="hasItemsPermissionProgramManager"
        text="Internships"
        :router-link="{ name: 'program-manager-internships' }"
        data-test-id="internships"
        @menu-item-clicked="handleMenuItemClick"
        mui-icon-name="business_center"
        :enable-selected-links="true"
        :count="counts?.get('program-manager-internships')"
      />
      <SidebarItem
        v-if="hasItemsPermissionProgramManager"
        text="Excursions + Events"
        :router-link="{ name: 'program-manager-events' }"
        data-test-id="events"
        @menu-item-clicked="handleMenuItemClick"
        mui-icon-name="local_activity"
        :enable-selected-links="true"
        :count="counts?.get('program-manager-events')"
      />
      <SidebarItem
        v-if="showHiddenNav"
        text="Content"
        :router-link="{ name: 'operations-dashboard' }"
        data-test-id="content"
        @menu-item-clicked="handleMenuItemClick"
        mui-icon-name="price_change"
        :enable-selected-links="true"
      />
      <SidebarItem
        v-if="hasItemsPermissionProgramManager"
        text="Classes"
        :router-link="{ name: 'program-manager-classes' }"
        data-test-id="classes"
        @menu-item-clicked="handleMenuItemClick"
        mui-icon-name="laptop_chromebook"
        :enable-selected-links="true"
        :count="counts?.get('program-manager-classes')"
      />
      <SidebarItem
        v-if="hasDisplayAdminUIForStudentDiscountsEnabled && hasDiscountPricingPermission"
        text="Custom Pricing"
        :router-link="{ name: 'custom-pricing' }"
        data-test-id="custom-pricing"
        @menu-item-clicked="handleMenuItemClick"
        mui-icon-name="price_change"
        :enable-selected-links="true"
      />
      <SidebarItem
        v-if="showHiddenNav"
        text="Badges"
        :router-link="{ name: 'operations-dashboard' }"
        data-test-id="badges"
        @menu-item-clicked="handleMenuItemClick"
        mui-icon-name="settings"
        :enable-selected-links="true"
      />
    </ul>
  </div>
</template>

<style scoped>

</style>
